import { useMemo } from 'react'
import { X } from 'lucide-react'
import YardMapCanvas from './YardMapCanvas'
import { calculateBoundingBox, initializeCameraState, CanvasOffset } from '../utils/yardMapUtils'
import { GisLayoutOrientation, YardMapFocusContext } from '../types/interchange'
import { useStageSize } from '../hooks/useStageSize'

interface YardMapOverlayModalProps {
  focusContext: YardMapFocusContext
  geoData: any | null
  yardAssets: any[]
  yardOrientation: GisLayoutOrientation
  activeTheme: 'midnight' | 'foundry' | 'daylight' | 'cyber'
  onClose: () => void
}

export default function YardMapOverlayModal({ focusContext, geoData, yardAssets, yardOrientation, activeTheme, onClose }: YardMapOverlayModalProps) {
  const stageSize = useStageSize({ widthFactor: 1, heightOffset: 64 })
  const canvasOffset: CanvasOffset = { x: 0, y: 0 }

  // Derive auto-zoom camera centered on the focused entity
  const cameraState = useMemo(() => {
    if (!geoData) return null

    const features = geoData.features
    // Match by ID (UUID) or name (human-readable) to support both formats
    const focused = features.find((f: any) =>
      ((focusContext.targetEntityType === 'SWITCH' && f.properties.type === 'switch') ||
       (focusContext.targetEntityType === 'TRACK_SEGMENT' && f.properties.type === 'track')) &&
      (f.id === focusContext.targetEntityName || f.properties.name === focusContext.targetEntityName)
    )

    if (focused) {
      const coords = focused.geometry.coordinates
      const centerCoord = focused.geometry.type === 'Point'
        ? coords as number[]
        : (coords as number[][])[Math.floor((coords as number[][]).length / 2)]

      return {
        centerLon: centerCoord[0],
        centerLat: centerCoord[1],
        zoomScale: focusContext.targetEntityType === 'SWITCH' ? 800000 : 400000
      }
    } else {
      // Fallback: fit full yard
      const tracks = features.filter((f: any) => f.properties.type === 'track')
      const switches = features.filter((f: any) => f.properties.type === 'switch')
      const bbox = calculateBoundingBox([...tracks, ...switches])
      return initializeCameraState(bbox, stageSize.width, stageSize.height)
    }
  }, [geoData, focusContext, stageSize])

  const isLoading = !geoData || !cameraState

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-950/95">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-900 shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-white">🗺️ Yard Map — Spatial Context</span>
          <span className="px-2 py-0.5 rounded text-xs font-mono bg-red-900/60 text-red-300 border border-red-700">
            {focusContext.errorCode}
          </span>
          <span className="text-sm text-slate-400">
            {focusContext.targetEntityType}: <strong className="text-white">{focusContext.targetEntityName}</strong>
          </span>
        </div>
        <button onClick={onClose} className="p-2 rounded hover:bg-slate-700 transition-colors" aria-label="Close yard map overlay">
          <X className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      {/* Error Context Banner */}
      <div className="px-6 py-2 bg-red-950/60 border-b border-red-800 text-sm text-red-300 shrink-0">
        {focusContext.errorMessage}
      </div>

      {/* Canvas Area */}
      <div className="flex-1 overflow-hidden">
        {isLoading && (
          <div className="flex items-center justify-center h-full text-slate-400">Loading spatial data...</div>
        )}
        {!isLoading && cameraState && (
          <YardMapCanvas
            geoData={geoData}
            yardAssets={yardAssets}
            activeTheme={activeTheme}
            cameraState={cameraState}
            canvasOffset={canvasOffset}
            stageSize={stageSize}
            yardOrientation={yardOrientation}
            enableContinuousScaling={false}
            targetEntityName={focusContext.targetEntityName}
            targetEntityType={focusContext.targetEntityType}
            highlightConfig={focusContext.highlightConfig}
            relatedEntities={focusContext.relatedEntities}
            simulationData={focusContext.simulationData}
          />
        )}
      </div>
    </div>
  )
}
